// Site → Canvas launch adapter (owner requests A and B, 5 Oct 2026).
//
// Binds the pure launch-link decision (`lib/sim/canvas/siteLaunchLink`) to the
// editor store, and keeps the in-memory record of WHICH Canvas a Site opened.
// That record is session-only on purpose: it is navigation context, not model
// data, so it is never written into a tab, a workspace save or a cloud doc.
import { create } from 'zustand';
import type { Site as EditorSite } from '../simulation/types';
import {
  applySiteLaunchLink,
  type SiteCanvasLaunch,
  type SiteLaunchLinkResult,
} from '../lib/sim/canvas/siteLaunchLink';
import { canvasTabMutationBlocker, useStore } from './index';

interface SiteCanvasLaunchState {
  launch: SiteCanvasLaunch | null;
  recordLaunch: (launch: SiteCanvasLaunch) => void;
  clearLaunch: () => void;
}

export const useSiteCanvasLaunch = create<SiteCanvasLaunchState>((set) => ({
  launch: null,
  recordLaunch: (launch) => set({ launch }),
  clearLaunch: () => set({ launch: null }),
}));

/**
 * Link the ACTIVE Canvas to the Site it was opened from (unless it is already
 * linked elsewhere or read-only) and record the launch. Every Site → editor
 * entry point calls this right after activating the tab it opens.
 */
export function openActiveCanvasFromSite(launchSite: EditorSite): SiteLaunchLinkResult {
  return applySiteLaunchLink(
    {
      getActiveTab: () => {
        const state = useStore.getState();
        // Defensive `?? []`: host shells and tests may mount without a tab list.
        const tab = (state.tabs ?? []).find((candidate) => candidate.id === state.activeTabId);
        return tab
          ? { id: tab.id, site: tab.site, mutationBlocker: canvasTabMutationBlocker(tab) }
          : undefined;
      },
      setSite: (site) => useStore.getState().setSite(site),
      recordLaunch: (launch) => useSiteCanvasLaunch.getState().recordLaunch(launch),
    },
    launchSite,
  );
}
